import image1 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_img_1.png';
import image2 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_img_2.png';
import image4 from '../../../../assets/images/work/norcat-mine-experience/NORCATMineExperience_detailed_abovefooter.png';

import artwork from '../../../../assets/images/art_2.png';
import eoLogoLowJpg from '../../../../assets/images/eologo-low.webp';
import eoLogoJpg from '../../../../assets/images/eologo.webp';

const Data_NORCATMineExperience = {
    content: (
        <>
            <div className="text-content">
                <div className="text-content-wrapper position-relative bg-black">
                    <div className="text-content-inner">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-6">
                                    <h4 className="heading heading-small light-2">PROBLEM</h4>

                                    <div className="paragraph light-1">
                                        <p>
                                            For most learners, understanding how an underground hard-rock mine operates is difficult
                                            without direct site exposure. Access limits, safety constraints, and logistics make it hard
                                            to provide consistent first-hand orientation at an active mine.
                                        </p>
                                        <br />
                                        <p>
                                            NORCAT needed a way to deliver foundational mining awareness virtually, while preserving the
                                            realism, terminology, and sequence of operations learners would encounter on site.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-lg-6 second-column">
                                    <h4 className="heading heading-small light-2">CHALLENGES</h4>

                                    <div className="paragraph light-1">
                                        <p>
                                            The experience had to simplify a complex operating environment without losing accuracy. It
                                            needed to introduce technical concepts like mine access methods, ventilation, right-of-way,
                                            emergency systems, and the drill-blast-muck-ground support cycle in a way that stays clear for
                                            new learners.
                                        </p>
                                        <br />
                                        <p>
                                            It also required a realistic narrative flow, from tagging in and PPE checks to underground
                                            travel and safe exit, so users could understand not just what happens in a mine, but why each
                                            control matters.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="project-content">
                <div className="project-content-wrapper">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="project-content-inner">
                                    <h2 className="heading heading-large dark-1">
                                        A standalone virtual mine ride that builds practical awareness of the mining lifecycle.
                                    </h2>

                                    <div className="paragraph dark-1">
                                        <p>
                                            The NORCAT Mine Experience places users alongside a guide at the NORCAT Underground Centre and
                                            walks them through a full underground journey, from arrival at the adit portal to tagging out
                                            on surface. The format is designed to make users feel virtually present at an active mine while
                                            learning the structure and language of modern hard-rock operations.
                                        </p>
                                    </div>

                                    <div className="heading heading-medium dark-1">
                                        <p>Scenario-Based Learning Flow</p>
                                    </div>

                                    <div className="paragraph dark-1">
                                        <p>
                                            The tour is organized as a sequence of guided scenarios that mirror real workflows:
                                            orientation and PPE, mine access methods, underground driving protocols, critical
                                            infrastructure, production drift observations, and controlled exit procedures.
                                        </p>
                                    </div>

                                    <div className="text-list medium dark-1">
                                        <ul>
                                            <li>Arrival briefing with core mining terms and roles</li>
                                            <li>PPE verification, cap lamp checks, and tag-in accountability</li>
                                            <li>Shaft, ramp, and adit access method overview</li>
                                            <li>Ventilation, DPM awareness, BEV charging, and electrical systems</li>
                                            <li>Emergency pull stations, refuge stations, and shop operations</li>
                                            <li>Production cycle context: drill, load/blast, ventilation, scaling, mucking, and bolting</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="single-image-wrapper">
                        <div className="single-image">
                            <img
                                src={image1}
                                alt="Learner perspective inside the NORCAT Mine Experience showing underground mine orientation content"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="single-image">
                            <img
                                src={image2}
                                alt="Underground mine scene from the NORCAT Mine Experience featuring guided training context"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>

                    <div className="container">
                        <div className="row">
                            <div className="col-lg-8">
                                <div className="project-content-inner">
                                    <h2 className="heading heading-medium dark-1">
                                        Designed to connect safety, process, and purpose
                                    </h2>

                                    <div className="paragraph dark-1">
                                        <p>
                                            Beyond equipment and terminology, the experience reinforces the core priorities of underground
                                            work: safety, communication, and situational awareness. By showing how each stage connects to
                                            the next, users gain a stronger understanding of why the mining cycle is repeatable, controlled,
                                            and central to safe production.
                                        </p>
                                        <br />
                                        <p>
                                            The result is a practical orientation tool that can support onboarding, education, and public
                                            awareness by making mining operations approachable before users ever enter a live heading.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="single-image">
                        <img
                            src={image4}
                            alt="Wide underground environment from the NORCAT Mine Experience highlighting mine systems and workflow"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>

            <div className="text-content">
                <div className="text-content-wrapper position-relative bg-black">
                    <div className="text-content-inner">
                        <div className="container">
                            <div className="row">
                                <div className="col-lg-6">
                                    <h4 className="heading heading-small light-2">RESULTS</h4>

                                    <div className="paragraph light-1">
                                        <p>
                                            The NORCAT Mine Experience provides a clear, structured introduction to underground mining in a
                                            virtual format that can be delivered consistently and repeatedly. Learners can explore the full
                                            lifecycle in context and build confidence with core concepts before field exposure.
                                        </p>
                                        <br />
                                        <p>
                                            By combining realistic mine environments with guided scenario narration, the program supports
                                            safer onboarding and stronger foundational awareness for students, trainees, and new workforce
                                            entrants.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-lg-5 offset-lg-1 second-column">
                                    <div className="content-wrapper">
                                        <h4 className="heading heading-small light-2">PLATFORM</h4>
                                        <div className="paragraph light-1">
                                            <p>PC VR</p>
                                        </div>
                                    </div>

                                    <div className="content-wrapper">
                                        <h4 className="heading heading-small light-2">CLIENT</h4>
                                        <div className="paragraph light-1">
                                            <p>NORCAT / Internal</p>
                                        </div>
                                    </div>

                                    <div className="content-wrapper eo-acknowledgement">
                                        <picture>
                                            <source media="(max-width: 1024px)" srcSet={eoLogoLowJpg} />
                                            <img
                                                className="eo-logo-primary"
                                                src={eoLogoJpg}
                                                alt="This Employment Ontario initiative is funded in part by the Government of Canada and the Government of Ontario."
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </picture>
                                        <p>
                                            This{' '}
                                            <a href="https://www.ontario.ca/page/employment-ontario" target="_blank" rel="noreferrer">
                                                Employment Ontario
                                            </a>{' '}
                                            initiative is funded in part by the{' '}
                                            <a href="https://www.canada.ca/en.html" target="_blank" rel="noreferrer">
                                                Government of Canada
                                            </a>{' '}
                                            and the{' '}
                                            <a href="https://www.ontario.ca/page/government-ontario" target="_blank" rel="noreferrer">
                                                Government of Ontario
                                            </a>
                                            .
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="text-content-footer">
                        <div className="container">
                            <div className="row align-items-lg-center">
                                <div className="col-md-6">
                                    <h3 className="heading heading-large light-1">
                                        Have an idea?
                                        <br />
                                        Let's explore it together.
                                    </h3>
                                </div>
                                <div className="col-md-6 d-md-inline-flex justify-content-md-end">
                                    <div className="button-wrapper">
                                        <a className="button button-1" href="/Contact">
                                            Contact Us
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="artwork artwork-2" aria-hidden="true">
                        <img src={artwork} alt="" loading="lazy" decoding="async" />
                    </div>
                </div>
            </div>
        </>
    ),
};

export default Data_NORCATMineExperience;
